import React, { useCallback, useState, useEffect } from 'react';
import InputWithLabel from '../../common/InputWithLabel';
import CommonSelect from '../../common/select/CommonSelect';
import DragDropFileInput from '../../common/DragDropFileInput';
import { PlayIcon, XMarkIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import {
  errorToast,
  successToast,
  getFileType,
  formatExerciseDuration
} from '../../../utils/helper';
import { validateSingleExercise } from '../../../validation/AddEditExerciseVali';
import { saveExercise, updateExercise } from '../../../api/healthAPI';
import { useHealthLoading } from '../../../context/HealthLoadingContext';
import useEquipments from '../hooks/useEquipments';
import axios from 'axios';

// Enums from Backend Constant.js - WORKOUT_MUSCLE_GROUP
const MUSCLE_GROUPS = [
  { label: 'Arms', value: 'Arms' },
  { label: 'Hips', value: 'Hips' },
  { label: 'Abs', value: 'Abs' },
  { label: 'Legs', value: 'Legs' },
  { label: 'Back', value: 'Back' },
  { label: 'Chest', value: 'Chest' },
  { label: 'Core', value: 'Core' },
  { label: 'Shoulders', value: 'Shoulders' },
  { label: 'Full Body', value: 'Full Body' },
  { label: 'Upper Body', value: 'Upper Body' },
  { label: 'Lower Body', value: 'Lower Body' }
];

const SingleExerciseForm = ({
  exercise,
  onExerciseChange,
  onSave,
  onCancel, // Add onCancel callback for canceling edit mode
  isView = false,
  showSaveButton = true,
  exerciseErrors = {},
  type = 'exercise', // Add type prop with default value
  isEditing = false, // Add isEditing prop to know if we're editing
  exerciseId = null // Add exerciseId prop for editing
}) => {
  const {
    equipments,
    loading: equipmentsLoading,
    error: equipmentsError,
    refreshEquipments
  } = useEquipments();

  const { withLoading } = useHealthLoading();

  // Get dynamic titles based on type
  const getTitle = (isView, type) => {
    if (type === 'stretch') {
      return isView ? 'View Stretch' : 'Add Stretch';
    }
    return isView ? 'View Exercise' : 'Add Exercise';
  };

  const getNameLabel = (type) => {
    return type === 'stretch' ? 'Stretch Name' : 'Exercise Name';
  };

  const getNamePlaceholder = (type) => {
    return type === 'stretch'
      ? 'Enter name: e.g Morning Back Stretch'
      : 'Enter name: e.g Dumbbell Shoulder Press';
  };

  const getVideoLabel = (type) => {
    return type === 'stretch' ? 'Stretch Video' : 'Exercise Video';
  };

  const getImageLabel = (type) => {
    return type === 'stretch' ? 'Stretch Image' : 'Exercise Image';
  };

  const getDurationLabel = (type) => {
    return type === 'stretch' ? 'Stretch Duration (Seconds)' : 'Exercise Duration (Seconds)';
  };

  const getSaveButtonText = (type, saving, isEditing) => {
    if (saving) return 'Saving...';
    if (isEditing) {
      return type === 'stretch' ? 'Update Stretch' : 'Update Exercise';
    }
    return type === 'stretch' ? 'Save Stretch' : 'Save Exercise';
  };

  // Loading state for save operation
  const [saving, setSaving] = useState(false);

  const handleFieldChange = useCallback(
    (field, value) => {
      const updatedExercise = { ...exercise, [field]: value };
      onExerciseChange(updatedExercise);
    },
    [exercise, onExerciseChange]
  );

  const handleSelectChange = useCallback(
    (field, selectedOption) => {
      let value;
      if (field === 'muscle_groups') {
        value = Array.isArray(selectedOption)
          ? selectedOption.map((option) => option.value)
          : selectedOption
          ? [selectedOption.value]
          : [];
      } else {
        value = selectedOption?.value || '';
      }
      handleFieldChange(field, value);
    },
    [handleFieldChange]
  );

  const handleImageUpload = useCallback(
    async (file) => {
      if (!file) return;

      if (!/\.(jpg|jpeg|png|gif)$/i.test(file.name)) {
        errorToast('Please upload a valid image file (JPG, JPEG, PNG, GIF)');
        return;
      }

      if (file.size > 1024 * 1024) {
        errorToast('Image file size should be less than 1MB');
        return;
      }

      // Show local preview immediately
      const localImageUrl = URL.createObjectURL(file);
      const updatedExercise = {
        ...exercise,
        image: file,
        imagePreview: localImageUrl,
        uploadingImage: false
      };
      onExerciseChange(updatedExercise);
      successToast('Image selected successfully');
    },
    [exercise, onExerciseChange]
  );

  const handleVideoUpload = useCallback(
    async (file) => {
      if (!file) return;

      if (!/\.(mp4|avi|mov|webm)$/i.test(file.name)) {
        errorToast('Please upload a valid video file (MP4, AVI, MOV, WEBM)');
        return;
      }

      const maxSize = 200 * 1024 * 1024; // 200MB
      if (file.size > maxSize) {
        const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
        errorToast(`Video file size (${fileSizeMB}MB) exceeds the maximum limit of 200MB`);
        return;
      }

      // Show local preview immediately
      const localVideoUrl = URL.createObjectURL(file);
      console.log('Video selected, creating blob URL:', localVideoUrl);

      const updatedExercise = {
        ...exercise,
        video: file,
        videoPreview: localVideoUrl,
        uploadingVideo: false
      };
      console.log('Updated exercise with video preview:', updatedExercise);
      onExerciseChange(updatedExercise);
      successToast('Video selected successfully');
    },
    [exercise, onExerciseChange]
  );

  const handleRemoveImage = useCallback(() => {
    if (exercise?.imagePreview?.startsWith('blob:')) {
      URL.revokeObjectURL(exercise.imagePreview);
    }
    // Update all image-related fields at once
    const updatedExercise = {
      ...exercise,
      image: null,
      image_url: '',
      imagePreview: ''
    };
    onExerciseChange(updatedExercise);
  }, [exercise, onExerciseChange]);

  const handleRemoveVideo = useCallback(() => {
    if (exercise?.videoPreview?.startsWith('blob:')) {
      URL.revokeObjectURL(exercise.videoPreview);
    }
    // Update all video-related fields at once
    const updatedExercise = {
      ...exercise,
      video: null,
      video_url: '',
      videoPreview: ''
    };
    onExerciseChange(updatedExercise);
  }, [exercise, onExerciseChange]);

  const handleSaveExercise = async () => {
    await withLoading(async () => {
      const { errors, isValid } = validateSingleExercise(exercise);

      if (!isValid) {
        const firstErrorMessage = Object.values(errors)[0];
        errorToast(firstErrorMessage);
        return;
      }

      try {
        setSaving(true);

        // Format data according to API requirements
        const exercisePayload = {
          name: exercise.name,
          muscle_groups: exercise.muscle_groups || [],
          exercise_duration: parseInt(exercise.exercise_duration) || 0
        };

        // Add equipment_id if present
        if (exercise.equipment_id) {
          exercisePayload.equipment_id = exercise.equipment_id;
        }

        // Add ID for update operations
        if (isEditing && exerciseId) {
          exercisePayload.id = exerciseId;
        }

        // Handle video upload
        if (exercise.video) {
          // New video file selected - send file type for presigned URL
          exercisePayload.video_url = getFileType(exercise.video);
        }
        // In edit mode without new upload, don't send video_url field
        // Backend will keep existing video unchanged

        // Handle image upload
        if (exercise.image) {
          // New image file selected - send file type for presigned URL
          exercisePayload.image_url = getFileType(exercise.image);
        }
        // In edit mode without new upload, don't send image_url field
        // Backend will keep existing image unchanged

        // Remove empty or undefined fields
        Object.keys(exercisePayload).forEach((key) => {
          if (exercisePayload[key] === '' || exercisePayload[key] === undefined) {
            delete exercisePayload[key];
          }
        });

        console.log('Saving exercise with payload:', exercisePayload);

        // Use updateExercise if editing, otherwise saveExercise
        const response =
          isEditing && exerciseId
            ? await updateExercise(exerciseId, exercisePayload)
            : await saveExercise(exercisePayload);

        if (response?.meta?.code === 1) {
          // Upload files using presigned URLs if available
          if (response?.meta?.videoPreSignUrl?.uploadURL && exercise.video) {
            await axios.put(response.meta.videoPreSignUrl.uploadURL, exercise.video, {
              headers: {
                'content-type': `${exercise.video.type.split('/')[0]}/${
                  exercise.video.name.split('.')[1]
                }`
              }
            });
          }

          if (response?.meta?.imagePreSignUrl?.uploadURL && exercise.image) {
            await axios.put(response.meta.imagePreSignUrl.uploadURL, exercise.image, {
              headers: {
                'content-type': `${exercise.image.type.split('/')[0]}/${
                  exercise.image.name.split('.')[1]
                }`
              }
            });
          }

          // Call the parent onSave callback if provided
          if (onSave) {
            onSave(response.data);
          }
        }
      } catch (error) {
        console.error('Error saving/updating exercise:', error);
        const errorMessage = isEditing
          ? type === 'stretch'
            ? 'Failed to update stretch'
            : 'Failed to update exercise'
          : type === 'stretch'
          ? 'Failed to save stretch'
          : 'Failed to save exercise';
        errorToast(error.message || errorMessage);
      } finally {
        setSaving(false);
      }
    });
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (exercise?.videoPreview?.startsWith('blob:')) {
        URL.revokeObjectURL(exercise.videoPreview);
      }
      if (exercise?.imagePreview?.startsWith('blob:')) {
        URL.revokeObjectURL(exercise.imagePreview);
      }
    };
  }, [exercise]);

  if (!exercise) {
    return (
      <div className="bg-white dark:bg-shoorah-darkBgTabColor rounded-lg p-6">
        <p className="text-gray-500 dark:text-gray-400">No exercise data available</p>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-shoorah-darkBgTabColor rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          {getTitle(isView, type)}
        </h3>
      </div>

      {/* Exercise Form Fields */}
      <div className="grid lg:gap-6 gap-4 md:grid-cols-2 grid-cols-1 mb-4">
        {/* Exercise/Stretch Name */}
        <div>
          <InputWithLabel
            label={getNameLabel(type)}
            placeholder={getNamePlaceholder(type)}
            onChange={(e) => handleFieldChange('name', e.target.value)}
            labelMargin="mb-2"
            labelClassName="text-sm font-medium text-gray-700 dark:text-shoorah-darkBg"
            value={exercise.name || ''}
            name="exercise-name"
            id="exercise-name"
            disabled={isView}
            isRequired={true}
            error={exerciseErrors.name}
          />
        </div>

        {/* Equipment */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="font-medium text-gray-700 dark:text-shoorah-darkBg text-sm">
              Equipment
            </label>
            {equipmentsError && (
              <button
                type="button"
                onClick={refreshEquipments}
                disabled={equipmentsLoading}
                className="flex items-center gap-1 text-xs text-shoorah-primary hover:text-shoorah-secondary disabled:opacity-50 disabled:cursor-not-allowed"
                title="Retry loading equipment"
              >
                <ArrowPathIcon className={`w-4 h-4 ${equipmentsLoading ? 'animate-spin' : ''}`} />
                Retry
              </button>
            )}
          </div>
          <CommonSelect
            disabled={isView || equipmentsLoading}
            name="equipment"
            onChange={(e) => handleSelectChange('equipment_id', e)}
            value={
              equipments?.find(
                (option) =>
                  option?.value === (exercise.equipment_id?._id || exercise.equipment_id || '')
              ) || null
            }
            options={equipments}
            placeholder={equipmentsLoading ? 'Loading equipment...' : 'Select equipment'}
            isLoading={equipmentsLoading}
            isClearable={true}
          />
          {equipmentsError && (
            <span className="text-red-500 text-[12px] mt-1 block">
              Failed to load equipment. Click retry to try again.
            </span>
          )}
          {exerciseErrors.equipment_id && (
            <span className="text-red-500 text-[12px] mt-1 block">
              {exerciseErrors.equipment_id}
            </span>
          )}
        </div>

        {/* Muscle Groups */}
        <div>
          <label className="font-medium text-gray-700 dark:text-shoorah-darkBg mb-2 block text-sm">
            Muscle Groups <span className="text-red-400">&#42;</span>
          </label>
          <CommonSelect
            disabled={isView}
            name="muscle-groups"
            isMulti={true}
            onChange={(e) => handleSelectChange('muscle_groups', e)}
            value={exercise.muscle_groups
              ?.map((group) => MUSCLE_GROUPS?.find((option) => option?.value === group))
              .filter(Boolean)}
            options={MUSCLE_GROUPS}
            placeholder="Select muscle groups"
          />
          {exerciseErrors.muscle_groups && (
            <span className="text-red-500 text-[12px] mt-1 block">
              {exerciseErrors.muscle_groups}
            </span>
          )}
        </div>

        {/* Exercise/Stretch Duration */}
        <div>
          {isView ? (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-shoorah-darkBg mb-2">
                {getDurationLabel(type)}
              </label>
              <div className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-shoorah-darkBgColor text-sm text-gray-900 dark:text-white">
                {formatExerciseDuration(exercise.exercise_duration) || '—'}
              </div>
            </div>
          ) : (
            <InputWithLabel
              label={getDurationLabel(type)}
              placeholder="Enter duration in seconds: e.g 90"
              onChange={(e) => handleFieldChange('exercise_duration', e.target.value)}
              labelMargin="mb-2"
              labelClassName="text-sm font-medium text-gray-700 dark:text-shoorah-darkBg"
              value={exercise.exercise_duration || ''}
              name="exercise-duration"
              id="exercise-duration"
              type="number"
              min="1"
              disabled={false}
              error={exerciseErrors.exercise_duration}
            />
          )}
        </div>
      </div>

      {/* Video and Image Upload Section */}
      <div className="grid lg:gap-6 gap-4 md:grid-cols-2 grid-cols-1 mb-4">
        {/* Video Upload */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-shoorah-darkBg mb-2">
            {getVideoLabel(type)} <span className="text-red-400">&#42;</span>
          </label>

          {!exercise.videoPreview && !exercise.video_url ? (
            <DragDropFileInput
              accept="video/mp4,video/avi,video/mov,video/webm"
              hint="MP4, AVI, MOV, WEBM (Max 200MB)"
              icon={
                <div className="flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 dark:bg-shoorah-darkBgColor mb-4">
                  <PlayIcon className="w-8 h-8 text-shoorah-primary" />
                </div>
              }
              onChange={(file) => handleVideoUpload(file)}
              disabled={isView}
              className="h-[250px]"
            />
          ) : (
            <div className="relative w-full h-[250px] flex items-center justify-center border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden bg-white dark:bg-shoorah-darkBgTabColor">
              <video
                key={exercise.videoPreview || exercise.video_url}
                controls
                preload="metadata"
                className="m-auto h-full object-center"
              >
                <source
                  src={exercise.videoPreview || exercise.video_url}
                  type={exercise.video?.type || 'video/mp4'}
                />
                Your browser does not support the video tag.
              </video>
              {exercise.uploadingVideo && (
                <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                  <div className="text-white text-sm">Uploading video...</div>
                </div>
              )}
              {!isView && (
                <button
                  type="button"
                  onClick={handleRemoveVideo}
                  className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-2 hover:bg-red-600 z-10"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              )}
            </div>
          )}
          {exerciseErrors.video && (
            <span className="text-red-500 text-[12px] mt-1 block">{exerciseErrors.video}</span>
          )}
        </div>

        {/* Image Upload */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-shoorah-darkBg mb-2">
            {getImageLabel(type)} <span className="text-red-400">&#42;</span>
          </label>

          {!exercise.imagePreview && !exercise.image_url ? (
            <DragDropFileInput
              accept="image/jpeg,image/jpg,image/png,image/gif"
              hint="JPG, JPEG, PNG, GIF (Max 1MB)"
              icon={
                <div className="flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 dark:bg-shoorah-darkBgColor mb-4">
                  <svg
                    className="w-8 h-8 text-shoorah-primary"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                </div>
              }
              onChange={(file) => handleImageUpload(file)}
              disabled={isView}
              className="h-[250px]"
            />
          ) : (
            <div className="relative rounded-lg overflow-hidden border border-gray-300 dark:border-gray-600">
              <img
                src={exercise.imagePreview || exercise.image_url}
                alt="Exercise preview"
                className="w-full h-[200px] object-cover"
              />
              {!isView && (
                <button
                  type="button"
                  onClick={handleRemoveImage}
                  className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-2 hover:bg-red-600"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              )}
            </div>
          )}
          {exerciseErrors.image && (
            <span className="text-red-500 text-[12px] mt-1 block">{exerciseErrors.image}</span>
          )}
        </div>
      </div>

      {!isView && showSaveButton && (
        <div className="flex justify-end gap-3">
          {isEditing && onCancel && (
            <button
              type="button"
              onClick={onCancel}
              disabled={saving}
              className="px-6 py-2 bg-white dark:bg-shoorah-darkBgTabColor border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-full text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
          )}
          <button
            type="button"
            onClick={handleSaveExercise}
            disabled={saving}
            className="btn-primary"
          >
            {getSaveButtonText(type, saving, isEditing)}
          </button>
        </div>
      )}
    </div>
  );
};

export default SingleExerciseForm;
